import { useEffect, useState } from "react";
import { Icon } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";

import BasicTable from "ui-components/tables/BasicTable";
import { ListLayout } from "ui-components/Layout";
import { EditButton } from "ui-components/IconButton";
import { Success, Error, MessageDialog } from "ui-components/Alert";
import BasicTabs from "ui-components/BasicTabs";
import { useCustomerValue } from "providers/CustomerContext";
import { getCOList } from "services/data.inbound.service";
import { getCOStatusCount } from "services/data.inbound.service";
import { deleteCO } from "services/data.inbound.service";
import COLog from "./components/COLog";
import { CancelButton } from "ui-components/IconButton";
import { TableCard } from "ui-components/Layout";
import { statusObj } from "layouts/views/common/enum";
import { useTeamValue } from "providers/TeamContext";

function COList() {
  const {permissions, allowAll} = useTeamValue();
  const { customerCode } = useCustomerValue();
  const enabled = customerCode !== null;
  const navigate = useNavigate();
  const location = useLocation();
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [data, setData] = useState([]);
  const [status, setStatus] = useState("Open");
  const [tabs, setTabs] = useState([
    { label: "Open", count: 0 },
    { label: "Cancelled", count: 0 },
    { label: "Received", count: 0 },
  ]);
  const queryClient = useQueryClient();
  const onError = (data) => setAlert(<Error data={data} onChange={setAlert} />);

  const addPermission = allowAll || (Boolean(permissions["Collection Order"]) && permissions["Collection Order"].findIndex(b => b==="new") !== -1);
  const editPermission = allowAll || (Boolean(permissions["Collection Order"]) && permissions["Collection Order"].findIndex(b => b==="edit") !== -1);
  const deactivatePermission = allowAll || (Boolean(permissions["Collection Order"]) && permissions["Collection Order"].findIndex(b => b==="deactivate") !== -1);

  useEffect(() => {
    if (location.state) {
      setAlert(<Success content={location.state} onChange={setAlert} />);
    }
  }, []);
  useQuery({
    queryKey: ["co-list", { customerCode, status }],
    queryFn: () => getCOList({ customerCode, status: statusObj[status] }),
    enabled,
    onSuccess: (data) => {
      setData(data.data.data);
    },
    onError,
  });

  useQuery({
    queryKey: ["co-list", "status", { customerCode }],
    queryFn: () => getCOStatusCount({ customerCode }),
    enabled,
    onSuccess: (data) => setTabs(data.data.data),
    onError,
  });

  const delMutation = useMutation(deleteCO, {
    onSuccess: (data) => {
      queryClient.invalidateQueries(["co-list"]);
      setAlert(<Success data={data} onChange={setAlert} />);
    },
    onError,
  });

  const onEdit = (req) => navigate("/inbound/co/edit", { state: req });

  const onDelete = (req) => {
    setDialog(
      <MessageDialog
        title={`Cancel CO Number ${req.coNumber}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          delMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };

  const [logRequest, setLogRequest] = useState(null);
  const [open, setOpen] = useState(false);
  const logProp = { open, setOpen, logRequest, setLogRequest };

  const columns = [
    {
      header: "CO Number",
      accessorKey: "coNumber",
      cell: ({ row }) => {
        const { coNumber, customerCode } = row.original;
        return (
          <span
            style={{ color: "#1A73E8", cursor: "pointer", textDecoration: "underline" }}
            onClick={() => {
              setOpen(true);
              setLogRequest({ coNumber, customerCode });
            }}
          >
            {coNumber}
          </span>
        );
      },
    },
    { header: "CO Date", accessorKey: "coDate" },
    { header: "Consignee Code", accessorKey: "consigneeCode" },
    { header: "Consignee Name", accessorKey: "cgCompany" },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { coNumber, customerCode } = row.original;
        return status === "Open" ? (
          <>
            <EditButton title="edit co" disabled={!editPermission} onClick={() => onEdit({ coNumber, customerCode })} />
            <CancelButton title="cancel co" disabled={!deactivatePermission} onClick={() => onDelete({ coNumber, customerCode })} />
          </>
        ) : (
          <></>
        );
      },
    },
  ];
  return (
    <ListLayout>
      <COLog {...logProp} />
      {dialog}
      <MDBox my={3}>
        {alert}
        <BasicTabs tabs={tabs} status={status} setStatus={setStatus} />
      </MDBox>
      <TableCard>
        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" margin="10px">
          <MDButton variant="gradient" color="info" disabled={!addPermission} onClick={() => navigate("/inbound/co/add")}>
            <Icon>add</Icon>&nbsp;co
          </MDButton>
          <MDButton
            sx={{ marginLeft: "1rem", borderRadius: "5px" }}
            variant="outlined"
            color="dark"
            disabled={!addPermission}
            onClick={() => navigate("/inbound/co/upload")}
          >
            <Icon>upload</Icon>&nbsp; co
          </MDButton>
        </MDBox>
        <BasicTable columns={columns} data={data} />
      </TableCard>
    </ListLayout>
  );
}

export default COList;
